import { useEffect, useState } from "react";
import { Row, Col, Card, Typography, Tag, Table, Avatar, Empty, Spin, Statistic } from "antd";
import {
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CarOutlined,
    DollarOutlined,
    EnvironmentOutlined,
    ArrowRightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useFetchTravellerProfileInfo, useGetMyTripList } from "../services/traveller.service";
import { storage } from "../utils/storage";

const { Title, Text } = Typography;

const PAGE_BG = "#f5f6fa";
const CARD_BG = "#ffffff";

const statusColors: Record<string, string> = {
    Pending: "orange",
    Upcoming: "blue",
    Running: "green",
    Completed: "cyan",
    Cancelled: "red",
};

const TravellerDashboard = () => {
    const navigate = useNavigate();
    const userId = storage.getUserId() ?? "";

    const [profile, setProfile] = useState<any>(null);
    const [allTrips, setAllTrips] = useState<any[]>([]);
    const [upcomingTrips, setUpcomingTrips] = useState<any[]>([]);
    const [completedCount, setCompletedCount] = useState(0);
    const [totalSpent, setTotalSpent] = useState(0);
    const [loading, setLoading] = useState(true);

    const fetchProfile = useFetchTravellerProfileInfo({
        onSuccess: (res: any) => {
            if (res?.statusCode === 200) setProfile(res.data);
        },
    });

    const fetchAllTrips = useGetMyTripList({
        onSuccess: (res: any) => {
            if (res?.statusCode === 200) {
                const trips: any[] = res.data ?? [];
                setAllTrips(trips.slice(0, 5));
                setCompletedCount(trips.filter((t: any) => t.tripStatus === "Completed").length);
                const spent = trips
                    .filter((t: any) => t.tripStatus === "Completed")
                    .reduce((sum: number, t: any) => sum + (parseFloat(t.finalChargeAmount ?? "0")), 0);
                setTotalSpent(spent);
            }
        },
    });

    const fetchUpcoming = useGetMyTripList({
        onSuccess: (res: any) => {
            if (res?.statusCode === 200) setUpcomingTrips((res.data ?? []).slice(0, 3));
            setLoading(false);
        },
        onError: () => setLoading(false),
    });

    useEffect(() => {
        if (!userId) return;
        fetchProfile.mutate(userId);
        fetchAllTrips.mutate({ pageNumber: 1, pageSize: 50, filterType: "all", searchFilter: "" });
        fetchUpcoming.mutate({ pageNumber: 1, pageSize: 3, filterType: "upcoming", searchFilter: "" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const totalTrips = allTrips.length;

    const upcomingSpendData = upcomingTrips.slice(0, 6).map((t: any, i: number) => ({
        name: t.cities ?? `Trip ${i + 1}`,
        hours: t.hours ?? 0,
    }));

    const statCards = [
        {
            title: "Total Trips",
            value: totalTrips,
            icon: <CalendarOutlined style={{ fontSize: 24, color: "#6366f1" }} />,
            color: "#6366f1",
            bg: "#eef2ff",
        },
        {
            title: "Upcoming",
            value: upcomingTrips.length,
            icon: <ClockCircleOutlined style={{ fontSize: 24, color: "#f59e0b" }} />,
            color: "#f59e0b",
            bg: "#fffbeb",
        },
        {
            title: "Completed",
            value: completedCount,
            icon: <CheckCircleOutlined style={{ fontSize: 24, color: "#10b981" }} />,
            color: "#10b981",
            bg: "#ecfdf5",
        },
        {
            title: "Total Spent",
            value: `$${totalSpent.toFixed(2)}`,
            icon: <DollarOutlined style={{ fontSize: 24, color: "#22d3ee" }} />,
            color: "#22d3ee",
            bg: "#e0f7fa",
        },
    ];

    const columns = [
        {
            title: "Attendant",
            dataIndex: "agentName",
            render: (name: string, record: any) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar size={34} src={record.agentImageUrl} style={{ backgroundColor: "#6366f1" }}>
                        {(name ?? "A").charAt(0)}
                    </Avatar>
                    <Text style={{ fontSize: 13, fontWeight: 500 }}>{name ?? "—"}</Text>
                </div>
            ),
        },
        {
            title: "City",
            dataIndex: "cities",
            render: (city: string) => (
                <span>
                    <EnvironmentOutlined style={{ color: "#6366f1", marginRight: 4 }} />
                    {city ?? "—"}
                </span>
            ),
        },
        {
            title: "Date",
            dataIndex: "tripStartDate",
            render: (d: string) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—",
        },
        {
            title: "Status",
            dataIndex: "tripStatus",
            render: (status: string) => (
                <Tag color={statusColors[status] ?? "default"}>{status ?? "—"}</Tag>
            ),
        },
        {
            title: "",
            key: "action",
            render: (_: any, record: any) => (
                <ArrowRightOutlined
                    style={{ color: "#6366f1", cursor: "pointer" }}
                    onClick={() => navigate(`/my-trips`)}
                />
            ),
        },
    ];

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                <Spin size="large" />
            </div>
        );
    }

    const displayName = profile?.fullName || storage.getEmailId()?.split("@")[0] || "Traveller";

    return (
        <div style={{ background: PAGE_BG }}>
            {/* Welcome banner */}
            <div
                style={{
                    background: "linear-gradient(135deg, #1a1a2e 0%, #6366f1 100%)",
                    borderRadius: 16,
                    padding: "28px 32px",
                    marginBottom: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <div>
                    <Title level={3} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                        Welcome back, {displayName}! 👋
                    </Title>
                    <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 14, marginTop: 4, display: "block" }}>
                        Here's an overview of your travel activity.
                    </Text>
                </div>
                <CarOutlined style={{ fontSize: 56, color: "rgba(255,255,255,0.2)" }} />
            </div>

            {/* KPI cards */}
            <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
                {statCards.map((card) => (
                    <Col xs={24} sm={12} lg={6} key={card.title}>
                        <Card
                            bodyStyle={{ padding: "20px 24px" }}
                            style={{ background: CARD_BG, borderRadius: 14, border: "none", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}
                        >
                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                                <div>
                                    <Text style={{ color: "#64748b", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                        {card.title}
                                    </Text>
                                    <div style={{ marginTop: 6 }}>
                                        <Statistic
                                            value={card.value}
                                            valueStyle={{ fontSize: 28, fontWeight: 700, color: "#1e293b" }}
                                        />
                                    </div>
                                </div>
                                <div
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 12,
                                        background: card.bg,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {card.icon}
                                </div>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
                {/* Upcoming trips mini chart */}
                <Col xs={24} lg={14}>
                    <Card
                        title={
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>
                                <ClockCircleOutlined style={{ marginRight: 8, color: "#6366f1" }} />
                                Upcoming Trips
                            </span>
                        }
                        extra={
                            <Text
                                style={{ color: "#6366f1", cursor: "pointer", fontSize: 13 }}
                                onClick={() => navigate("/my-trips")}
                            >
                                View all →
                            </Text>
                        }
                        bodyStyle={{ padding: "16px 20px" }}
                        style={{ borderRadius: 14, border: "none", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}
                    >
                        {upcomingTrips.length > 0 ? (
                            <div>
                                {upcomingSpendData.length > 1 && (
                                    <ResponsiveContainer width="100%" height={140}>
                                        <AreaChart data={upcomingSpendData} margin={{ top: 4, right: 8, left: -28, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                                            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                                            <Tooltip />
                                            <Area type="monotone" dataKey="hours" stroke="#6366f1" fill="#eef2ff" strokeWidth={2} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                )}
                                <div style={{ marginTop: 12 }}>
                                    {upcomingTrips.map((trip: any, idx: number) => (
                                        <div
                                            key={idx}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                padding: "10px 0",
                                                borderBottom: idx < upcomingTrips.length - 1 ? "1px solid #f1f5f9" : "none",
                                            }}
                                        >
                                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                <div
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: 10,
                                                        background: "#eef2ff",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <EnvironmentOutlined style={{ color: "#6366f1", fontSize: 16 }} />
                                                </div>
                                                <div>
                                                    <Text strong style={{ fontSize: 13 }}>{trip.cities ?? "Trip"}</Text>
                                                    <Text
                                                        style={{ fontSize: 12, color: "#94a3b8", display: "block" }}
                                                    >
                                                        {trip.tripStartDate
                                                            ? new Date(trip.tripStartDate).toLocaleDateString("en-GB", {
                                                                day: "2-digit",
                                                                month: "short",
                                                                year: "numeric",
                                                            })
                                                            : "Date TBD"}
                                                    </Text>
                                                </div>
                                            </div>
                                            <Tag color="blue">{trip.tripStatus ?? "Upcoming"}</Tag>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <Empty description={<Text style={{ color: "#94a3b8" }}>No upcoming trips</Text>} imageStyle={{ height: 60 }} />
                        )}
                    </Card>
                </Col>

                {/* Profile quick view */}
                <Col xs={24} lg={10}>
                    <Card
                        title={
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>
                                <CarOutlined style={{ marginRight: 8, color: "#10b981" }} />
                                Your Profile
                            </span>
                        }
                        extra={
                            <Text
                                style={{ color: "#6366f1", cursor: "pointer", fontSize: 13 }}
                                onClick={() => navigate("/my-profile")}
                            >
                                Edit →
                            </Text>
                        }
                        bodyStyle={{ padding: "16px 20px" }}
                        style={{ borderRadius: 14, border: "none", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}
                    >
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "12px 0" }}>
                            <Avatar
                                size={72}
                                src={profile?.imageUrl}
                                style={{ background: "linear-gradient(135deg, #1a1a2e, #6366f1)", fontSize: 28, marginBottom: 12 }}
                            >
                                {displayName.charAt(0).toUpperCase()}
                            </Avatar>
                            <Title level={5} style={{ margin: 0, color: "#1e293b" }}>{displayName}</Title>
                            <Text style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16 }}>{storage.getEmailId()}</Text>

                            {[
                                { label: "Gender", value: profile?.gender ?? "—" },
                                { label: "Phone", value: profile?.phoneNo ? `+${profile.phoneNoDialingCode ?? ""} ${profile.phoneNo}` : "—" },
                                { label: "Status", value: profile?.travellerStatusName ?? "Active" },
                            ].map((item) => (
                                <div
                                    key={item.label}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        padding: "8px 0",
                                        borderBottom: "1px solid #f1f5f9",
                                    }}
                                >
                                    <Text style={{ color: "#94a3b8", fontSize: 13 }}>{item.label}</Text>
                                    <Text style={{ color: "#1e293b", fontSize: 13, fontWeight: 500 }}>{item.value}</Text>
                                </div>
                            ))}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Recent trips table */}
            <Card
                title={
                    <span style={{ fontWeight: 600, color: "#1e293b" }}>
                        <CalendarOutlined style={{ marginRight: 8, color: "#6366f1" }} />
                        Recent Trips
                    </span>
                }
                extra={
                    <Text
                        style={{ color: "#6366f1", cursor: "pointer", fontSize: 13 }}
                        onClick={() => navigate("/my-trips")}
                    >
                        View all →
                    </Text>
                }
                bodyStyle={{ padding: "0 4px" }}
                style={{ borderRadius: 14, border: "none", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}
            >
                <Table
                    dataSource={allTrips}
                    columns={columns}
                    rowKey={(r) => r.tripId ?? Math.random()}
                    pagination={false}
                    size="small"
                    locale={{ emptyText: <Empty description="No trips yet" imageStyle={{ height: 60 }} /> }}
                />
            </Card>
        </div>
    );
};

export default TravellerDashboard;
